import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSite } from "../contexts/SiteContext";
import { useAuth } from "../contexts/AuthContext";
import { mediaUrl } from "../lib/api";

export const Header = ({ onOpenLead }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { settings, tabs } = useSite();
  const { user } = useAuth();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-3xl bg-[#020205]/30 border-b border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_-1px_0_rgba(0,240,255,0.15),inset_0_1px_0_rgba(255,255,255,0.12)]"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          data-testid="brand-logo-link"
        >
          <img
            src={mediaUrl(settings.logo_url)}
            alt={settings.brand_name}
            className="h-12 md:h-16 w-auto object-contain drop-shadow-[0_0_22px_rgba(255,214,10,0.5)] transition-transform duration-300 group-hover:scale-105 logo-shimmer"
            data-testid="brand-logo-img"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
          {tabs.map((t) => (
            <NavLink
              key={t.id || t.path}
              to={t.path}
              data-testid={`nav-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-[#00F0FF] bg-white/5"
                    : "text-zinc-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
          {user && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-[#00FF66] bg-white/5"
                    : "text-zinc-400 hover:text-[#00FF66] hover:bg-white/5"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="header-cta-automate-btn"
            onClick={() => onOpenLead?.()}
            className="relative bg-gradient-to-br from-[#00F0FF] via-[#33F3FF] to-[#9C0FD9] text-black px-5 py-2 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-[0_8px_24px_-6px_rgba(0,240,255,0.7),inset_0_1px_0_rgba(255,255,255,0.5),inset_0_-2px_4px_rgba(0,0,0,0.15)]"
          >
            Start Automating
          </button>
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen((s) => !s)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-2">
            {tabs.map((t) => (
              <button
                key={t.id || t.path}
                data-testid={`mobile-nav-${t.label.toLowerCase()}`}
                onClick={() => {
                  setOpen(false);
                  navigate(t.path);
                }}
                className="text-left text-zinc-200 py-2 text-sm font-medium hover:text-[#00F0FF]"
              >
                {t.label}
              </button>
            ))}
            {user && (
              <button
                data-testid="mobile-nav-admin"
                onClick={() => {
                  setOpen(false);
                  navigate("/admin");
                }}
                className="text-left text-zinc-400 py-2 text-sm font-medium hover:text-[#00FF66]"
              >
                Admin
              </button>
            )}
            <button
              data-testid="mobile-cta-automate-btn"
              onClick={() => {
                setOpen(false);
                onOpenLead?.();
              }}
              className="bg-[#00F0FF] text-black px-5 py-2 rounded-full text-sm font-bold mt-2"
            >
              Start Automating
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
